// Projects.js
import React, { useState, useEffect, useRef } from 'react';
import './Projects.css';
import p1 from '../Assets/p1.jpg';
import p2 from '../Assets/p2.jpg';
import p3 from '../Assets/p3.jpg';
import p4 from '../Assets/p4.jpg';
import p5 from '../Assets/p5.png';
import p6 from '../Assets/p6.jpg';

const Projects = ({ language = 'en' }) => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');
  const [hoveredProject, setHoveredProject] = useState(null);
  const projectsRef = useRef([]);

  // Translations
  const translations = {
    en: {
      heroTitle: 'Our Projects',
      heroSubtitle: 'Transforming spaces with premium natural stone',
      filterAll: 'All Projects',
      filterResidential: 'Residential',
      filterCommercial: 'Commercial',
      filterHospitality: 'Hospitality',
      viewDetails: 'View Details',
      projectsCompleted: 'Projects Completed',
      countriesServed: 'Countries Served',
      yearsExperience: 'Years of Experience',
      clientSatisfaction: 'Client Satisfaction',
      
      // Project Details
      project1: {
        title: 'Luxury Bathroom Suite',
        category: 'Residential',
        location: 'Mumbai, India',
        stone: 'Beige Travertine',
        description: 'An elegant bathroom transformation featuring our premium beige travertine with book-matched wall panels and custom vanity tops.',
        features: ['Book-matched panels', 'Custom vanity design', 'Seamless installation', 'Water-resistant finish']
      },
      project2: {
        title: 'Modern Shower Sanctuary',
        category: 'Residential',
        location: 'Dubai, UAE',
        stone: 'Ivory Marble',
        description: 'A minimalist shower space showcasing the natural beauty of ivory marble with precision-cut panels and integrated lighting.',
        features: ['Floor-to-ceiling marble', 'Integrated LED lighting', 'Custom shower fixtures', 'Non-slip treatment']
      },
      project3: {
        title: 'Contemporary Kitchen Island',
        category: 'Hospitality',
        location: 'London, UK',
        stone: 'Blue Quartzite',
        description: 'A stunning kitchen centerpiece featuring rare blue quartzite with waterfall edges and integrated bar seating.',
        features: ['Waterfall edge design', 'Integrated bar seating', 'Heat-resistant surface', 'Custom edge profile']
      },
      project4: {
        title: 'Grand Entrance Hall',
        category: 'Commercial',
        location: 'New York, USA',
        stone: 'Grey Granite',
        description: 'A majestic entrance featuring dramatic grey granite columns with decorative panels and custom architectural elements.',
        features: ['Fluted columns', 'Decorative wall panels', 'Custom millwork', 'Premium polish finish']
      },
      project5: {
        title: 'Decorative Planter Feature',
        category: 'Hospitality',
        location: 'Singapore',
        stone: 'White Limestone',
        description: 'An artistic limestone planter with distinctive ribbed texture, bringing natural elegance to modern architecture.',
        features: ['Ribbed texture design', 'Weather-resistant', 'Custom dimensions', 'Sustainable materials']
      },
      project6: {
        title: 'Presidential Installation',
        category: 'Commercial',
        location: 'Washington D.C., USA',
        stone: 'American Granite',
        description: 'A monumental sculpture carved from premium American granite, showcasing exceptional craftsmanship and attention to detail.',
        features: ['Hand-carved details', 'Weather-resistant finish', 'Structural engineering', 'Historic significance']
      }
    },
    zh: {
      heroTitle: '我们的项目',
      heroSubtitle: '用优质天然石材改造空间',
      filterAll: '所有项目',
      filterResidential: '住宅',
      filterCommercial: '商业',
      filterHospitality: '酒店',
      viewDetails: '查看详情',
      projectsCompleted: '完成的项目',
      countriesServed: '服务国家',
      yearsExperience: '年经验',
      clientSatisfaction: '客户满意度',
      
      project1: {
        title: '豪华浴室套房',
        category: '住宅',
        location: '印度孟买',
        stone: '米色洞石',
        description: '优雅的浴室改造，采用我们的优质米色洞石，配有对称墙板和定制梳妆台。',
        features: ['对称面板', '定制梳妆台设计', '无缝安装', '防水处理']
      },
      project2: {
        title: '现代淋浴圣地',
        category: '住宅',
        location: '阿联酋迪拜',
        stone: '象牙大理石',
        description: '极简主义淋浴空间，展示象牙大理石的自然美，配有精密切割面板和集成照明。',
        features: ['落地大理石', '集成LED照明', '定制淋浴装置', '防滑处理']
      },
      project3: {
        title: '现代厨房岛台',
        category: '住宅',
        location: '英国伦敦',
        stone: '蓝色石英岩',
        description: '令人惊叹的厨房中心，采用稀有蓝色石英岩，配有瀑布边缘和集成吧台座椅。',
        features: ['瀑布边设计', '集成吧台座椅', '耐热表面', '定制边缘轮廓']
      },
      project4: {
        title: '大堂入口',
        category: '商业',
        location: '美国纽约',
        stone: '灰色花岗岩',
        description: '宏伟的入口，配有戏剧性的灰色花岗岩柱，装饰面板和定制建筑元素。',
        features: ['凹槽柱', '装饰墙板', '定制木工', '优质抛光']
      },
      project5: {
        title: '装饰花盆特色',
        category: '酒店',
        location: '新加坡',
        stone: '白色石灰石',
        description: '艺术石灰石花盆，具有独特的肋纹理，为现代建筑带来自然优雅。',
        features: ['肋纹理设计', '耐候性', '定制尺寸', '可持续材料']
      },
      project6: {
        title: '总统装置',
        category: '商业',
        location: '美国华盛顿特区',
        stone: '美国花岗岩',
        description: '由优质美国花岗岩雕刻的纪念性雕塑，展示了卓越的工艺和对细节的关注。',
        features: ['手工雕刻细节', '耐候处理', '结构工程', '历史意义']
      }
    },
    it: {
      heroTitle: 'I Nostri Progetti',
      heroSubtitle: 'Trasformare gli spazi con pietra naturale premium',
      filterAll: 'Tutti i Progetti',
      filterResidential: 'Residenziale',
      filterCommercial: 'Commerciale',
      filterHospitality: 'Ospitalità',
      viewDetails: 'Vedi Dettagli',
      projectsCompleted: 'Progetti Completati',
      countriesServed: 'Paesi Serviti',
      yearsExperience: 'Anni di Esperienza',
      clientSatisfaction: 'Soddisfazione Cliente',
      
      project1: {
        title: 'Suite Bagno di Lusso',
        category: 'Residenziale',
        location: 'Mumbai, India',
        stone: 'Travertino Beige',
        description: 'Un\'elegante trasformazione del bagno con il nostro travertino beige premium con pannelli a libro e piani lavabo personalizzati.',
        features: ['Pannelli a libro', 'Design lavabo personalizzato', 'Installazione senza giunture', 'Finitura resistente all\'acqua']
      },
      project2: {
        title: 'Santuario Doccia Moderno',
        category: 'Residenziale',
        location: 'Dubai, EAU',
        stone: 'Marmo Avorio',
        description: 'Uno spazio doccia minimalista che mostra la bellezza naturale del marmo avorio con pannelli tagliati con precisione e illuminazione integrata.',
        features: ['Marmo dal pavimento al soffitto', 'Illuminazione LED integrata', 'Apparecchi doccia personalizzati', 'Trattamento antiscivolo']
      },
      project3: {
        title: 'Isola Cucina Contemporanea',
        category: 'Residenziale',
        location: 'Londra, UK',
        stone: 'Quarzite Blu',
        description: 'Un sorprendente centrotavola da cucina con rara quarzite blu con bordi a cascata e posti a sedere integrati.',
        features: ['Design bordo a cascata', 'Sedute bar integrate', 'Superficie resistente al calore', 'Profilo bordo personalizzato']
      },
      project4: {
        title: 'Grande Sala d\'Ingresso',
        category: 'Commerciale',
        location: 'New York, USA',
        stone: 'Granito Grigio',
        description: 'Un ingresso maestoso con drammatiche colonne di granito grigio con pannelli decorativi ed elementi architettonici personalizzati.',
        features: ['Colonne scanalate', 'Pannelli murali decorativi', 'Lavorazione personalizzata', 'Finitura lucida premium']
      },
      project5: {
        title: 'Caratteristica Fioriera Decorativa',
        category: 'Ospitalità',
        location: 'Singapore',
        stone: 'Pietra Calcarea Bianca',
        description: 'Una fioriera artistica in pietra calcarea con texture a coste distintiva, che porta eleganza naturale all\'architettura moderna.',
        features: ['Design texture a coste', 'Resistente alle intemperie', 'Dimensioni personalizzate', 'Materiali sostenibili']
      },
      project6: {
        title: 'Installazione Presidenziale',
        category: 'Commerciale',
        location: 'Washington D.C., USA',
        stone: 'Granito Americano',
        description: 'Una scultura monumentale scolpita in granito americano premium, che mostra eccezionale maestria e attenzione ai dettagli.',
        features: ['Dettagli scolpiti a mano', 'Finitura resistente alle intemperie', 'Ingegneria strutturale', 'Significato storico']
      }
    }
  };

  const t = translations[language] || translations.en;

  const projects = [
    { id: 1, image: p1, ...t.project1 },
    { id: 2, image: p2, ...t.project2 },
    { id: 3, image: p3, ...t.project3 },
    { id: 4, image: p4, ...t.project4 },
    { id: 5, image: p5, ...t.project5 },
    { id: 6, image: p6, ...t.project6 }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePosition({ x, y });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);

    // Animation observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      observer.disconnect();
    };
  }, []);

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.category.toLowerCase() === filter.toLowerCase());

  const openProjectModal = (project) => {
    setSelectedProject(project);
  };

  const closeProjectModal = () => {
    setSelectedProject(null);
  };

  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) {
      closeProjectModal();
    }
  };

  return (
    <div className="about-container">

      {/* Hero Section */}
      <section className="projects-hero">
        <div className="hero-background">
          <div 
            className="parallax-layer layer-1" 
            style={{ transform: `translateY(${scrollY * 0.3}px)` }}
          />
          <div 
            className="parallax-layer layer-2" 
            style={{ transform: `translateY(${scrollY * 0.2}px)` }}
          />
          <div 
            className="parallax-layer layer-3" 
            style={{ transform: `translateY(${scrollY * 0.1}px)` }}
          />
        </div>
        
        <div className="hero-content">
          <h1 className="hero-title">{t.heroTitle}</h1>
          <p className="hero-subtitle">{t.heroSubtitle}</p>
          
          {/* Animated Icon */}
          <div className="hero-icon">
            <div className="icon-circle">
              <div className="icon-inner">🏗️</div>
              <div className="icon-ring ring-1"></div>
              <div className="icon-ring ring-2"></div>
            </div>
          </div>
          
          {/* Stats Section */}
          <div className="stats-container">
            <div className="stat-item">
              <div className="stat-number" data-count="500">500+</div>
              <div className="stat-label">{t.projectsCompleted}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number" data-count="25">25+</div>
              <div className="stat-label">{t.countriesServed}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number" data-count="15">15+</div>
              <div className="stat-label">{t.yearsExperience}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">99%</div>
              <div className="stat-label">{t.clientSatisfaction}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="filter-section">
        <div className="filter-container animate-on-scroll">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            {t.filterAll}
          </button>
          <button 
            className={`filter-btn ${filter === 'residential' ? 'active' : ''}`}
            onClick={() => setFilter('residential')}
          >
            {t.filterResidential}
          </button>
          <button 
            className={`filter-btn ${filter === 'commercial' ? 'active' : ''}`}
            onClick={() => setFilter('commercial')}
          >
            {t.filterCommercial}
          </button>
          <button 
            className={`filter-btn ${filter === 'hospitality' ? 'active' : ''}`}
            onClick={() => setFilter('hospitality')}
          >
            {t.filterHospitality}
          </button>
        </div>
      </section>

      {/* Projects Gallery */}
      <section className="projects-gallery">
        <div className="gallery-container">
          {filteredProjects.map((project, index) => (
            <div 
              key={project.id}
              className={`project-card animate-on-scroll ${hoveredProject === project.id ? 'hovered' : ''}`}
              ref={el => projectsRef.current[index] = el}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
              onClick={() => openProjectModal(project)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="project-image-container">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="project-image"
                />
                <div className="project-overlay">
                  <div className="overlay-content">
                    <span className="project-category">{project.category}</span>
                    <h3 className="project-title">{project.title}</h3>
                    <button className="view-details-btn">
                      {t.viewDetails} →
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="project-info">
                <h3 className="project-name">{project.title}</h3>
                <p className="project-stone">{project.category}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Project Modal */}
      <div 
        className={`project-modal ${selectedProject ? 'active' : ''}`}
        onClick={handleModalClick}
      >
        {selectedProject && (
          <div className="modal-content">
            <button className="gallery-close" onClick={closeProjectModal}>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            
            <div className="modal-grid">
              <div className="modal-image-section">
                <img 
                  src={selectedProject.image} 
                  alt={selectedProject.title}
                  className="modal-image"
                />
              </div>
              
              <div className="modal-info-section">
                <span className="modal-category">{selectedProject.category}</span>
                <h2 className="modal-title">{selectedProject.title}</h2>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Floating Background Elements */}
      <div className="background-shapes">
        <div 
          className="shape shape-1"
          style={{ transform: `translate(${mousePosition.x * 30}px, ${mousePosition.y * 30}px)` }}
        />
        <div 
          className="shape shape-2"
          style={{ transform: `translate(${mousePosition.x * -25}px, ${mousePosition.y * -25}px)` }}
        />
        <div 
          className="shape shape-3"
          style={{ transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * -20}px)` }}
        />
        <div 
          className="shape shape-4"
          style={{ transform: `translate(${mousePosition.x * -15}px, ${mousePosition.y * 15}px)` }}
        />
      </div>
    </div>
  );
};

export default Projects;